/**
 * SaveQueue - IndexedDB-backed local storage queue
 *
 * Implements persistent queue for pending save operations using IndexedDB.
 * Provides FIFO ordering per document and in-memory fallback when IndexedDB is unavailable.
 *
 * @module features/storage/saveQueue
 * @see SPEC-006-background-cloud-save-queue
 * @see Requirements 1.1-1.8, 10.3, 10.4
 */

import { openDB, DBSchema, IDBPDatabase } from 'idb';
import type { PendingSave, PendingSaveStatus } from './types';

// Re-export PendingSave for convenience
export type { PendingSave, PendingSaveStatus };

/**
 * IndexedDB Schema for SaveQueue
 *
 * @see Requirements 1.2 - Store each Pending_Save with required fields
 */
export interface SaveQueueDB extends DBSchema {
  pendingSaves: {
    key: string; // saveId
    value: PendingSave;
    indexes: {
      'by-docId': string;
      'by-createdAt': number;
    };
  };
}

/**
 * Default database configuration
 */
export const SAVE_QUEUE_DB_NAME = 'linkode-save-queue';
export const SAVE_QUEUE_STORE_NAME = 'pendingSaves';
export const SAVE_QUEUE_DB_VERSION = 1;

/**
 * Options for SaveQueue initialization
 */
export interface SaveQueueOptions {
  /** Database name (default: 'linkode-save-queue') */
  dbName?: string;
  /** Store name (default: 'pendingSaves') */
  storeName?: string;
}

/**
 * Counter for generating unique save IDs within a session
 */
let saveIdCounter = 0;

/**
 * Generates a unique save ID for tracking save operations
 *
 * Format: `save-{timestamp}-{counter}-{random}`
 * This ensures uniqueness across browser sessions and tabs.
 *
 * @returns Unique save ID string
 * @see Requirements 1.3 - Assign unique saveId for tracking
 */
export function generateSaveId(): string {
  const timestamp = Date.now();
  const counter = ++saveIdCounter;
  const random = Math.random().toString(36).substring(2, 8);
  return `save-${timestamp}-${counter}-${random}`;
}

/**
 * SaveQueue - IndexedDB-backed persistent queue for save operations
 *
 * Features:
 * - Persistent storage using IndexedDB
 * - FIFO ordering per document
 * - In-memory fallback when IndexedDB is unavailable
 * - Automatic recovery on browser restart
 *
 * @see Requirements 1.1, 1.5, 1.6, 1.7
 */
export class SaveQueue {
  private db: IDBPDatabase<SaveQueueDB> | null = null;
  private inMemoryFallback: Map<string, PendingSave> = new Map();
  private useInMemory: boolean = false;
  private readonly dbName: string;
  private readonly storeName: string;

  constructor(options: SaveQueueOptions = {}) {
    this.dbName = options.dbName ?? SAVE_QUEUE_DB_NAME;
    this.storeName = options.storeName ?? SAVE_QUEUE_STORE_NAME;
  }

  /**
   * Initialize the SaveQueue by opening IndexedDB connection
   *
   * Falls back to in-memory storage if IndexedDB is unavailable.
   *
   * @see Requirements 1.1, 10.4
   */
  async init(): Promise<void> {
    try {
      this.db = await openDB<SaveQueueDB>(this.dbName, SAVE_QUEUE_DB_VERSION, {
        upgrade(db) {
          // Create object store with indexes
          const store = db.createObjectStore('pendingSaves', { keyPath: 'saveId' });
          store.createIndex('by-docId', 'docId', { unique: false });
          store.createIndex('by-createdAt', 'createdAtMs', { unique: false });
        },
        blocked() {
          console.warn('[SaveQueue] Database upgrade blocked by another connection');
        },
        blocking() {
          console.warn('[SaveQueue] This connection is blocking a database upgrade');
        },
        terminated() {
          console.error('[SaveQueue] Database connection terminated unexpectedly');
        },
      });
      this.useInMemory = false;
    } catch (error) {
      console.warn('[SaveQueue] IndexedDB unavailable, using in-memory fallback:', error);
      this.useInMemory = true;
    }
  }

  /**
   * Check if the queue is using in-memory fallback
   */
  isUsingInMemoryFallback(): boolean {
    return this.useInMemory;
  }

  /**
   * Enqueue a new save operation
   *
   * @param save - Save data (without saveId, createdAtMs, attempt, status)
   * @returns The assigned saveId
   * @see Requirements 1.1, 1.3, 1.6
   */
  async enqueue(
    save: Omit<PendingSave, 'saveId' | 'createdAtMs' | 'attempt' | 'status'>
  ): Promise<string> {
    const saveId = generateSaveId();
    const pendingSave: PendingSave = {
      ...save,
      saveId,
      createdAtMs: Date.now(),
      attempt: 0,
      status: 'pending',
    };

    if (this.useInMemory) {
      this.inMemoryFallback.set(saveId, pendingSave);
      return saveId;
    }

    // Guard against uninitialized db - fallback to in-memory
    if (!this.db) {
      this.inMemoryFallback.set(saveId, pendingSave);
      return saveId;
    }

    try {
      await this.db.put('pendingSaves', pendingSave);
      return saveId;
    } catch (error) {
      // Handle quota exceeded error
      if (this.isQuotaExceededError(error)) {
        console.error('[SaveQueue] IndexedDB quota exceeded, switching to in-memory');
        this.useInMemory = true;
        this.inMemoryFallback.set(saveId, pendingSave);
        return saveId;
      }
      throw error;
    }
  }

  /**
   * Remove a completed save from the queue
   *
   * @param saveId - ID of the save to remove
   * @see Requirements 1.6
   */
  async dequeue(saveId: string): Promise<void> {
    if (this.useInMemory) {
      this.inMemoryFallback.delete(saveId);
      return;
    }

    // Guard against uninitialized db
    if (!this.db) {
      return;
    }

    await this.db.delete('pendingSaves', saveId);
  }

  /**
   * Get the next pending save without removing it
   *
   * @param docId - Optional document ID to filter by
   * @returns The oldest pending save, or undefined if queue is empty
   * @see Requirements 1.6, 1.7
   */
  async peek(docId?: string): Promise<PendingSave | undefined> {
    if (this.useInMemory) {
      const saves = this.getInMemorySaves(docId);
      return saves[0];
    }

    // Guard against uninitialized db
    if (!this.db) {
      return undefined;
    }

    if (docId) {
      const saves = await this.db.getAllFromIndex('pendingSaves', 'by-docId', docId);
      // Sort by createdAtMs for FIFO order
      saves.sort((a, b) => a.createdAtMs - b.createdAtMs);
      return saves[0];
    }

    // Get all and return the oldest
    const saves = await this.db.getAllFromIndex('pendingSaves', 'by-createdAt');
    return saves[0];
  }

  /**
   * Get all pending saves
   *
   * @param docId - Optional document ID to filter by
   * @returns Array of pending saves in FIFO order
   * @see Requirements 1.6, 1.7
   */
  async getAll(docId?: string): Promise<PendingSave[]> {
    if (this.useInMemory) {
      return this.getInMemorySaves(docId);
    }

    // Guard against uninitialized db
    if (!this.db) {
      return [];
    }

    let saves: PendingSave[];
    if (docId) {
      saves = await this.db.getAllFromIndex('pendingSaves', 'by-docId', docId);
    } else {
      saves = await this.db.getAll('pendingSaves');
    }

    // Sort by createdAtMs for FIFO order
    saves.sort((a, b) => a.createdAtMs - b.createdAtMs);
    return saves;
  }

  /**
   * Get the number of items in the queue
   *
   * @param docId - Optional document ID to filter by
   * @returns Number of pending saves
   */
  async getQueueLength(docId?: string): Promise<number> {
    if (this.useInMemory) {
      return this.getInMemorySaves(docId).length;
    }

    // Guard against uninitialized db
    if (!this.db) {
      return 0;
    }

    if (docId) {
      const saves = await this.db.getAllFromIndex('pendingSaves', 'by-docId', docId);
      return saves.length;
    }

    return await this.db.count('pendingSaves');
  }

  /**
   * Helper to get in-memory saves filtered by docId
   */
  private getInMemorySaves(docId?: string): PendingSave[] {
    const saves = Array.from(this.inMemoryFallback.values());
    const filtered = docId ? saves.filter((s) => s.docId === docId) : saves;
    // Sort by createdAtMs for FIFO order
    return filtered.sort((a, b) => a.createdAtMs - b.createdAtMs);
  }

  /**
   * Check if an error is a quota exceeded error
   */
  private isQuotaExceededError(error: unknown): boolean {
    if (error instanceof DOMException) {
      // Different browsers use different error names
      return (
        error.name === 'QuotaExceededError' ||
        error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
        error.code === 22 // Legacy quota exceeded code
      );
    }
    return false;
  }

  /**
   * Update the status of a pending save
   *
   * @param saveId - ID of the save to update
   * @param status - New status
   * @param error - Optional error message (for 'failed' status)
   * @see Requirements 3.5
   */
  async updateStatus(saveId: string, status: PendingSaveStatus, error?: string): Promise<void> {
    if (this.useInMemory) {
      const save = this.inMemoryFallback.get(saveId);
      if (save) {
        save.status = status;
        if (error !== undefined) {
          save.lastError = error;
        }
      }
      return;
    }

    // Guard against uninitialized db
    if (!this.db) {
      return;
    }

    const save = await this.db.get('pendingSaves', saveId);
    if (save) {
      save.status = status;
      if (error !== undefined) {
        save.lastError = error;
      }
      await this.db.put('pendingSaves', save);
    }
  }

  /**
   * Increment the attempt count for a pending save
   *
   * @param saveId - ID of the save to update
   * @returns The new attempt count
   * @see Requirements 3.5
   */
  async incrementAttempt(saveId: string): Promise<number> {
    if (this.useInMemory) {
      const save = this.inMemoryFallback.get(saveId);
      if (save) {
        save.attempt += 1;
        return save.attempt;
      }
      return 0;
    }

    // Guard against uninitialized db
    if (!this.db) {
      return 0;
    }

    const save = await this.db.get('pendingSaves', saveId);
    if (save) {
      save.attempt += 1;
      await this.db.put('pendingSaves', save);
      return save.attempt;
    }
    return 0;
  }

  /**
   * Reset the attempt count for a pending save
   *
   * @param saveId - ID of the save to update
   * @see Requirements 3.5
   */
  async resetAttempt(saveId: string): Promise<void> {
    if (this.useInMemory) {
      const save = this.inMemoryFallback.get(saveId);
      if (save) {
        save.attempt = 0;
        save.status = 'pending';
        save.lastError = undefined;
      }
      return;
    }

    // Guard against uninitialized db
    if (!this.db) {
      return;
    }

    const save = await this.db.get('pendingSaves', saveId);
    if (save) {
      save.attempt = 0;
      save.status = 'pending';
      save.lastError = undefined;
      await this.db.put('pendingSaves', save);
    }
  }

  /**
   * Clear all pending saves from the queue
   *
   * @param docId - Optional document ID to clear only saves for that document
   */
  async clear(docId?: string): Promise<void> {
    if (this.useInMemory) {
      if (docId) {
        for (const [saveId, save] of this.inMemoryFallback) {
          if (save.docId === docId) {
            this.inMemoryFallback.delete(saveId);
          }
        }
      } else {
        this.inMemoryFallback.clear();
      }
      return;
    }

    // Guard against uninitialized db
    if (!this.db) {
      return;
    }

    if (docId) {
      const saves = await this.db.getAllFromIndex('pendingSaves', 'by-docId', docId);
      const tx = this.db.transaction('pendingSaves', 'readwrite');
      await Promise.all([...saves.map((save) => tx.store.delete(save.saveId)), tx.done]);
    } else {
      await this.db.clear('pendingSaves');
    }
  }

  /**
   * Get a specific save by ID
   *
   * @param saveId - ID of the save to retrieve
   * @returns The pending save, or undefined if not found
   */
  async get(saveId: string): Promise<PendingSave | undefined> {
    if (this.useInMemory) {
      return this.inMemoryFallback.get(saveId);
    }

    // Guard against uninitialized db
    if (!this.db) {
      return undefined;
    }

    return await this.db.get('pendingSaves', saveId);
  }

  /**
   * Close the database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}
